import { Component, OnInit } from '@angular/core';
import { SettingsService } from 'src/app/services/settings.service';
import { TransactionService } from 'src/app/services/transaction.service';
import { Transaction } from 'src/app/shared/models/transactions/transaction';
import * as XLSX from 'xlsx';
import { formatDate } from '@angular/common';
import { TransactionSearchRequest } from 'src/app/shared/models/transactions/transaction-search-request';

@Component({
  selector: 'app-trade-history',
  templateUrl: './trade-history.component.html',
  styleUrls: ['./trade-history.component.scss'],
})
export class TradeHistoryComponent implements OnInit {
  constructor(
    public settingsService: SettingsService,
    public transactionsService: TransactionService
  ) {}

  //General
  busy: boolean = true;
  showError: boolean = false;
  errorMsg = '';
  selectedDateRange = 1;

  //Trade transactions
  transactions: Transaction[] = [];
  selectedTransaction: Transaction = new Transaction();
  averageTradeProfitZar: number = 0;
  totalProfit: number = 0;
  totalFIAUsed: number = 0;
  totalSDAUsed: number = 0;
  averageProfitPercentage: number = 0;
  totalTraded: number = 0;

  public transactionSearchRequest: TransactionSearchRequest =
    new TransactionSearchRequest();
  public brokerId: number;

  DateFilter: { Id: number; Name: string }[] = [
    { Id: 1, Name: 'Current Calendar Year' },
    { Id: 2, Name: 'Current Tax Year' },
    { Id: 3, Name: 'Previous Calendar Year' },
    { Id: 4, Name: 'Previous Tax Year' },
    { Id: 5, Name: 'All' },
  ];

  ngOnInit(): void {
    this.busy = true;
    this.settingsService.ValidateGlobalVars().then(() => {
      // this.searchRequest.entityType = this.settingsService.userEntity.id;
      // this.searchRequest.id = this.settingsService.user.id;
      this.brokerId = this.settingsService.brokerUsers[0].brokerId;
      this.SetupUserTradingSearchRequest();
      this.GetTradeHistory();
    });
  }

  GetTradeHistory() {
    this.busy = true;
    this.transactionsService
      .SearchTransactions(this.transactionSearchRequest)
      .subscribe(
        (response) => {
          this.transactions = response;
          this.CalculateTotals();
          this.busy = false;
          this.showError = false;
        },
        (error) => {
          this.showError = true;
          this.errorMsg = error.error.ErrorDetail;
          this.busy = false;
        }
      );
  }

  SetupUserTradingSearchRequest() {
    this.transactionSearchRequest = new TransactionSearchRequest();
    this.transactionSearchRequest.transactionStatusId = -999;//-999 uses the default API filter which shows ALL
    this.transactionSearchRequest.userId =
      this.settingsService.userProfile.userId;
    this.transactionSearchRequest.dateFilter = this.DateFilter[0].Id; // Current Calendar Year
    this.transactionSearchRequest.brokerId = this.brokerId;
    this.transactionSearchRequest.includeDocuments = false;
    this.transactionSearchRequest.rows = 9999;
  }

  SelectTransaction(transaction: Transaction) {
    if (transaction) {
      this.selectedTransaction = transaction;
    }
  }

  CalculateTotals() {
    this.averageTradeProfitZar = 0;
    this.totalProfit = 0;
    this.totalFIAUsed = 0;
    this.totalSDAUsed = 0;
    this.totalTraded = 0;
    this.averageProfitPercentage = 0;

    if (this.transactions.length > 0) {
      let totalProfitPercentage: number = 0;
      this.transactions.forEach((x) => {
        this.totalTraded += x.amountSent
        this.totalProfit += x.totalProfit;
        this.totalFIAUsed += x.fiaAmount;
        this.totalSDAUsed += x.sdaAmount;
        totalProfitPercentage += x.totalProfitPercentage;
      });

      this.averageProfitPercentage = totalProfitPercentage / this.transactions.length;
      this.averageTradeProfitZar = this.totalProfit / this.transactions.length;
    }
  }

  toggleFiaPinsTable(trans: Transaction) {
    if (trans.selected) {
      trans.selected = false;
    } else {
      trans.selected = true;
    }
  }

  Extract(idselector: string) {
    const filename =
      this.settingsService.userProfile.fullName.replace(' ', '') +
      '_' +
      this.DateFilter[this.transactionSearchRequest.dateFilter - 1].Name.replace(' ', '') +
      '_TradeExport_';

    // Map the transaction data to a clean format for export
    const exportData = this.transactions.map((trans) => {
      return {
        '': '',
        Profit: trans.totalProfit,
        'Profit %': trans.totalProfitPercentage,
        'Trade Amount': trans.amountSent,
        'Start Date': formatDate(trans.transactionStartDate, 'yyyy-MM-dd', 'en'),
        'Completion Date': formatDate(trans.transactionEndDate, 'yyyy-MM-dd', 'en'),
        Status: trans.GetStatusName(),
        'FIA Amount': trans.fiaAmount,
        'SDA Amount': trans.sdaAmount,
      };
    });

    // blank row
    const blankRow = {
      '': '',
      Profit: null,
      'Profit %': null,
      'Trade Amount': null,
      'Start Date': '',
      'Completion Date': '',
      Status: '',
      'FIA Amount': null,
      'SDA Amount': null,
    };

    exportData.push(blankRow);

    // Create the total row object
    const totalRow = {
      '': 'Totals:',
      Profit: this.totalProfit,
      'Profit %': null,
      'Trade Amount': this.totalTraded,
      'Start Date': '',
      'Completion Date': '',
      Status: '',
      'FIA Amount': this.totalFIAUsed,
      'SDA Amount': this.totalSDAUsed,
    };

    // Add the total row to the export data
    exportData.push(totalRow);

    // Generate the worksheet from the JSON data
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);

    // Apply number formatting to the desired columns
    const range = XLSX.utils.decode_range(ws['!ref']);

    for (let R = range.s.r + 1; R <= range.e.r; ++R) {
      // R+1 to skip header row
      const profitCell = ws[XLSX.utils.encode_cell({ r: R, c: 1 })];
      if (profitCell) profitCell.z = '#,##0.00';
      
      const profitPercentCell = ws[XLSX.utils.encode_cell({ r: R, c: 2 })];
      if (profitPercentCell) profitPercentCell.z = '0.00';
      
      const tradeAmountCell = ws[XLSX.utils.encode_cell({ r: R, c: 3 })];
      if (tradeAmountCell) tradeAmountCell.z = '#,##0';

      const fiaAmountCell = ws[XLSX.utils.encode_cell({ r: R, c: 7 })];
      if (fiaAmountCell) fiaAmountCell.z = '#,##0';

      const sdaAmountCell = ws[XLSX.utils.encode_cell({ r: R, c: 8 })];
      if (sdaAmountCell) sdaAmountCell.z = '#,##0';
    }

    // Generate workbook and save the file
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, filename + formatDate(new Date(), 'yyyyMMdd', 'en') + '.xlsx');
  }
}
